import React, { useState, useEffect, useRef } from "react";
import { Modal } from "antd";
import {
  Box,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  styled
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Close,
  CheckCircle,
  Apple,
  Android
} from "@mui/icons-material";
import img10 from "../assets/img/image 10.png";
import img11 from "../assets/img/image 11.png";
import img6 from "../assets/img/image 6.png";


// Styled components
const SectionWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 1),
  color: "#ffffff",
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0, 0.5, 1, 0.5),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  margin: theme.spacing(0, 0, 0.5, 0.5),
  fontSize: "clamp(0.85rem, 1.8vw, 1.1rem)",
  fontWeight: 600,
  color: "#ffffff",
  [theme.breakpoints.down("sm")]: {
    fontSize: "clamp(0.75rem, 3.5vw, 0.9rem)",
    marginBottom: theme.spacing(0.25),
  },
}));

const ScrollWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(0, 0.5),
}));

const ScrollButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  width: 36,
  height: 36,
  backgroundColor: "#D4AF37",
  color: "#fff",
  boxShadow: "0 10px 25px rgba(212, 175, 55, 0.35)",
  zIndex: 15,
  left: -4,
  "&:hover": {
    backgroundColor: "#C5A028",
    transform: "translateY(-50%) scale(1.05)",
  },
  "&.Mui-disabled": {
    opacity: 0,
    visibility: "hidden",
  },
  [theme.breakpoints.down("sm")]: {
    width: 28,
    height: 28,
    left: 4,
  },
}));

const ScrollButtonRight = styled(ScrollButton)({
  left: "auto",
  right: -4,
});

const CardsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1.5),
  overflowX: "auto",
  padding: theme.spacing(0.5),
  scrollSnapType: "x mandatory",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  msOverflowStyle: "none",
  scrollbarWidth: "none",
}));

const Card = styled(Box)(({ theme }) => ({
  display: "flex",
  flex: "0 0 auto",
  alignItems: "center",
  width: 320,
  maxWidth: "85vw",
  background: "linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.02))",
  border: "1px solid rgba(255,255,255,0.06)",
  borderRadius: "9999px",
  padding: theme.spacing(0.6, 2),
  boxShadow: "0 6px 18px rgba(2,6,23,0.6)",
  cursor: "pointer",
  scrollSnapAlign: "start",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "scale(1.02)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
  },
  [theme.breakpoints.down("sm")]: {
    width: "clamp(85px, 22vw, 120px)",
    minWidth: 85,
    padding: theme.spacing(0.2, 0.3),
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    boxShadow: "none",
    borderRadius: 0,
  },
}));

const MediaContainer = styled(Box)(({ theme }) => ({
  flex: "0 0 64px",
  height: 64,
  marginRight: theme.spacing(0.6),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  padding: 2,
  background: "linear-gradient(135deg, #FFD700 0%, #DAA520 50%, #B8860B 100%)",
  border: "1px solid rgba(218, 165, 32, 0.5)",
  boxShadow: "0 6px 18px rgba(218, 165, 32, 0.3)",
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    flex: `0 0 clamp(48px, 15vw, 64px)`,
    height: "clamp(48px, 15vw, 64px)",
    marginRight: 0,
    marginBottom: theme.spacing(0.3),
  },
}));

const StyledImage = styled("img")(({ theme }) => ({
  width: 58,
  height: 58,
  objectFit: "cover",
  borderRadius: "50%",
  boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
  [theme.breakpoints.down("sm")]: {
    width: "clamp(42px, 13vw, 58px)",
    height: "clamp(42px, 13vw, 58px)",
  },
}));

const NewBadge = styled(Box)({
  position: "absolute",
  bottom: -8,
  left: "50%",
  transform: "translateX(-50%)",
  background: "linear-gradient(90deg, #F37021 0%, #FF3B52 50%, #ED0080 100%)",
  color: "white",
  fontSize: "0.7rem",
  fontWeight: 700,
  padding: "2px 10px",
  borderRadius: 12,
  whiteSpace: "nowrap",
  letterSpacing: "0.05em",
  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  zIndex: 2,
});

const CardBody = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    alignItems: "center",
    textAlign: "center",
  },
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "clamp(0.75rem, 1.5vw, 1.15rem)",
  marginBottom: theme.spacing(0.3),
  color: "#ffffff",
  textShadow: "0 1px 2px rgba(0,0,0,0.3)",
  letterSpacing: "0.02em",
  lineHeight: 1.3,
  [theme.breakpoints.down("sm")]: {
    fontSize: "clamp(0.5rem, 2.8vw, 0.65rem)",
    margin: "4px 0 0 0",
    lineHeight: 1.25,
    textAlign: "center",
  },
}));

const CardDescription = styled(Typography)(({ theme }) => ({
  fontSize: "clamp(0.7rem, 1.2vw, 0.9rem)",
  color: "rgba(255,255,255,0.8)",
  textAlign: "left",
  lineHeight: 1.4,
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

// Close button for antd Modal
const CloseButton = styled(IconButton)(({ theme }) => ({
  color: "#666",
  backgroundColor: "transparent",
  transition: "all 0.2s ease",
  "&:hover": {
    color: "#000",
    backgroundColor: "rgba(0, 0, 0, 0.06)",
  },
  "& .MuiSvgIcon-root": {
    fontSize: 20,
  },
}));

const BenefitList = styled(List)({
  padding: 0,
  margin: "24px 0",
});

const BenefitItem = styled(ListItem)({
  padding: "8px 0",
  alignItems: "flex-start",
});

const ActionButtons = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
  paddingTop: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
}));

const DEFAULT_ITEMS = [
  {
    label: "Mvest Personal Pensions",
    url: "https://www.oldmutual.com.gh/personal/mvest-personal-pension-scheme/",
    applyUrl: "https://www.oldmutual.com.gh/mvest/",
    learnMoreUrl: "https://www.oldmutual.com.gh/personal/mvest-personal-pension-scheme/",
    description: "Secure your future with our flexible pension scheme",
    modalDescription: "Secure your personal pension for the future",
    benefits: [
      "Competitive returns",
      "Lump-Sum payment",
      "Easy application and exit process",
      "Access to life insurance",
      "Access to personal loans",
      "Different and convenient modes of contribution",
      "Benefit payment is made within 72 hours",
      "24/7 online portal",
    ],
    image: "https://eu-images.contentstack.com/v3/assets/bltdd392fd32dda3ce8/blt3d1a427c7ce1fe4c/67977d588594a46945a82eea/Mvest_Personal_Pension_Scheme_card.jpg?width=288&height=146&format=webp&quality=100",
  },
  {
    label: "Travel Insurance",
    modalTitle: "International Travel Insurance",
    url: "https://www.oldmutual.com.gh/personal/international-travel-insurance/",
    applyUrl: "https://www.oldmutual.com.gh/travel-insurance/",
    learnMoreUrl: "https://www.oldmutual.com.gh/personal/international-travel-insurance/",
    description: "Travel loss insurance.",
    modalDescription: "Up to €30,000 emergency medical coverage abroad",
    benefits: [
      "Up to €30,000 emergency medical expenses cover",
      "€3,000 permanent total disability cover",
    ],
    image: img6,
  },
  {
    label: "Investment Plan",
    modalTitle: "Special Investment Plan",
    url: "https://www.oldmutual.com.gh/personal/special-investment-plan/",
    applyUrl: "https://www.oldmutual.com.gh/personal/special-investment-plan/",
    learnMoreUrl: "https://www.oldmutual.com.gh/personal/special-investment-plan/",
    description: "Long-term savings for retirement and other needs.",
    modalDescription:
      "A long-term savings policy for retirement and other needs, with added protection benefits.",
    benefits: [
      "Financial security for you and your family in old age",
      "Partial withdrawals: up to 50% once a year after 2 years in force",
      "Death cover for beneficiaries (higher of death cover or monthly contribution)",
      "Sum assured up to 120x monthly premium (max GH₵ 10,000)",
    ],
    image:
      "https://eu-images.contentstack.com/v3/assets/bltdd392fd32dda3ce8/bltb7b97d4bb2bff7b3/67977d5bf9a080c8249e4265/Special_Investment_Plan_hero.jpg?width=1312&height=540&format=webp&quality=100",
  },
  {
    label: "Educator Plan",
    url: "https://www.oldmutual.com.gh/personal/education-plan/",
    applyUrl: "https://www.oldmutual.com.gh/personal/education-plan/education-plan-form/",
    learnMoreUrl: "https://www.oldmutual.com.gh/personal/education-plan/",
    description: "Cover yourself, your parents and more",
    modalDescription: "Secure your child's education with life coverage and savings",
    benefits: [
      "Child Life Assurance Protection",
      "Accumulated Fund Payback after 24 months",
      "Parent Disability Protection",
      "Partial withdrawals after 2 years",
    ],
    image: img10,
  },
  {
    label: "Retirement Salary",
    modalTitle: "Old Mutual Retirement Salary",
    url: "https://www.oldmutual.com.gh/personal/old-mutual-retirement-salary/",
    applyUrl: "https://www.oldmutual.com.gh/personal/old-mutual-retirement-salary/",
    learnMoreUrl: "https://www.oldmutual.com.gh/personal/old-mutual-retirement-salary/",
    description: "Regular income for life after you retire.",
    modalDescription:
      "A guaranteed life annuity that pays you a regular income for as long as you're alive, with potential annual increases over time.",
    benefits: [
      "Guaranteed income for life",
      "Potential annual increases via a with-profits annuity",
      "Joint life option (where available)",
      "Funeral cover and beneficiary options (where applicable)",
    ],
    image:
      "https://eu-images.contentstack.com/v3/assets/bltdd392fd32dda3ce8/bltfa604776a506b07b/67977d9cd0c84d78c48c7bd4/Annuity_Product_hero_banner.jpg?width=1312&height=540&format=webp&quality=100",
  },
  {
    label: "Personal Accident",
    url: "https://www.oldmutual.com.gh/personal/personal-accident-policy/",
    applyUrl: "https://www.oldmutual.com.gh/personal-accident/",
    learnMoreUrl: "https://www.oldmutual.com.gh/personal/personal-accident-policy/",
    description: "Financial protection against accidental injuries and disabilities",
    modalDescription: "Financial protection against accidental injuries and disabilities",
    benefits: [
      "Accidental death",
      "Permanent disability of the insured arising from the accident.",
      "A temporary total disability that makes you lose your source of income.",
      "€3,000 permanent total disability cover",
    ],
    image: img11,
  },
  {
    label: "Legacy",
    url: "https://www.oldmutual.com.gh/legacy/",
    applyUrl: "https://www.oldmutual.com.gh/legacy/",
    learnMoreUrl: "https://www.oldmutual.com.gh/legacy/",
    description: "Secure your legacy for future generations",
    modalDescription: "Protect and preserve your legacy for your loved ones",
    benefits: [
      "Inheritance planning",
      "Estate management",
      "Wealth transfer strategies",
    ],
    image:
      "https://www.oldmutual.com.gh/v3/assets/bltdd392fd32dda3ce8/blt03ab9202530d6dad/67977dbb4581366e4ab5c8cf/Our_Strategy.jpg",
  },
];

function normalizeItems(items) {
  const source = Array.isArray(items) && items.length > 0 ? items : DEFAULT_ITEMS;
  return source.map((it, idx) => ({
    id: it.id || `item-${idx}`,
    image: it.image || "https://via.placeholder.com/64",
    label: it.label || it.title || "Item",
    title: it.title || it.label || "Item",
    description: it.description || "",
    href: it.href || it.url || "#",
    isNew: it.isNew || (it.label || "").toLowerCase().includes("mvest"),
    applyUrl: it.applyUrl || it.href || it.url || "#",
    learnMoreUrl: it.learnMoreUrl || it.href || it.url || "#",
    modalTitle: it.modalTitle || it.label || it.title,
    modalDescription: it.modalDescription || it.description || "",
    benefits: it.benefits || [],
  }));
}

export default function RecommendedForYou({ items = [], title = "" }) {
  const list = normalizeItems(items);
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState(null);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const updateScrollState = () => {
      const el = scrollRef.current;
      if (!el) return;
      setCanScrollLeft(el.scrollLeft > 8);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    };

    updateScrollState();
    container.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);
    return () => {
      container.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [list.length]);

  const scrollCards = (direction) => {
    const container = scrollRef.current;
    if (!container) return;
    const delta = direction === "left" ? -280 : 280;
    container.scrollBy({ left: delta, behavior: "smooth" });
  };

  const openModal = (item) => {
    setActive(item);
    setVisible(true);
  };

  const closeModal = () => {
    setVisible(false);
    setActive(null);
  };

  const renderLabel = (label) => {
    if (!label) return null;
    if (isMobile) {
      const words = label.split(/\s+/);
      const firstWord = words[0];
      const rest = words.slice(1).join(" ");
      if (words.length === 1) return label;
      if (label === "Special Investment Plan" || label === "Mvest Personal Pensions") {
        return label;
      }
      return (
        <>
          <Box component="span" display="block" fontSize="inherit">
            {firstWord}
          </Box>
          <Box component="span" display="block" fontSize="0.9em" color="rgba(255,255,255,0.95)">
            {rest}
          </Box>
        </>
      );
    }
    return label;
  };

  return (
    <SectionWrapper>
      <SectionTitle variant="h3">{title}</SectionTitle>

      <ScrollWrapper>
        <ScrollButton
          onClick={() => scrollCards("left")}
          disabled={!canScrollLeft}
          size="small"
        >
          <ChevronLeft />
        </ScrollButton>

        <CardsContainer ref={scrollRef}>
          {list.map((it) => (
            <Card key={it.id} onClick={() => openModal(it)}>
              <MediaContainer>
                {it.isNew && <NewBadge>NEW</NewBadge>}
                <StyledImage src={it.image} alt={it.label} />
              </MediaContainer>
              <CardBody>
                <CardTitle variant="body1">{renderLabel(it.label)}</CardTitle>
                <CardDescription variant="body2">{it.description}</CardDescription>
              </CardBody>
            </Card>
          ))}
        </CardsContainer>

        <ScrollButtonRight
          onClick={() => scrollCards("right")}
          disabled={!canScrollRight}
          size="small"
        >
          <ChevronRight />
        </ScrollButtonRight>
      </ScrollWrapper>

      {/* Antd Modal */}
      <Modal
        open={visible}
        onCancel={closeModal}
        closeIcon={<CloseButton><Close /></CloseButton>}
        footer={null}
        width={720}
        styles={{
          body: { padding: 0, maxHeight: '85vh', overflowY: 'auto' }
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <Typography variant="h6" fontWeight={800} color="#222" sx={{ pr: 4, mb: 0.5 }}>
            {active?.modalTitle || active?.label || active?.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {active?.modalDescription || active?.description || ""}
          </Typography>

          {active && active.benefits && active.benefits.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                Key Benefits
              </Typography>
              <BenefitList>
                {active.benefits.map((benefit, index) => (
                  <BenefitItem key={index} disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle sx={{ color: "#D4AF37", fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={benefit}
                      primaryTypographyProps={{ variant: "body2", color: "#000" }}
                    />
                  </BenefitItem>
                ))}
              </BenefitList>
            </>
          )}

          <ActionButtons>
            {active?.isNew && active?.label !== "Mvest Personal Pensions" ? (
              <>
                <Button
                  href="https://apps.apple.com/us/app/old-mutual/id1481355805"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  startIcon={<Apple />}
                  fullWidth={isMobile}
                  sx={{
                    borderRadius: "50px",
                    py: 1,
                    borderColor: "#D4AF37",
                    color: "#D4AF37",
                    "&:hover": {
                      borderColor: "#C5A028",
                      backgroundColor: "rgba(212, 175, 55, 0.04)",
                    },
                  }}
                >
                  Apple Store
                </Button>
                <Button
                  href={active.href || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  startIcon={<Android />}
                  fullWidth={isMobile}
                  sx={{
                    borderRadius: "50px",
                    py: 1,
                    bgcolor: "#D4AF37",
                    "&:hover": { bgcolor: "#C5A028" },
                  }}
                >
                  Play Store
                </Button>
              </>
            ) : (
              <>
                <Button
                  href={active?.applyUrl || active?.href || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  fullWidth={isMobile}
                  sx={{
                    borderRadius: "50px",
                    py: 1,
                    bgcolor: "#D4AF37",
                    "&:hover": { bgcolor: "#C5A028" },
                  }}
                >
                  Apply Now
                </Button>
                <Button
                  href={active?.learnMoreUrl || active?.href || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  fullWidth={isMobile}
                  sx={{
                    borderRadius: "50px",
                    py: 1,
                    borderColor: "#D4AF37",
                    color: "#D4AF37",
                    "&:hover": {
                      borderColor: "#C5A028",
                      backgroundColor: "rgba(212, 175, 55, 0.04)",
                    },
                  }}
                >
                  Learn More
                </Button>
              </>
            )}
          </ActionButtons>
        </Box>
      </Modal>
    </SectionWrapper>
  );
}
